'use client';

import { create } from 'zustand';
import { persist } from 'zustand/middleware';

import { NamesSteps } from '@/helpers/auth';

type FormData = Record<string, any>;

export enum PersistentFormName {
  PreRegistration = 'pre-registration',
  Registration = 'registration',
  PaymentMethodPersonalInfo = 'payment-method-personal-info',
  WithdrawPersonalInfo = 'withdraw-personal-info',
}

const InitialSteps = {
  [PersistentFormName.Registration]: NamesSteps.PRIMARY_INFORMATION,
  [PersistentFormName.PreRegistration]: '',
  [PersistentFormName.PaymentMethodPersonalInfo]: '',
  [PersistentFormName.WithdrawPersonalInfo]: '',
};

export interface PersistentFormStore {
  forms: Record<
    string,
    {
      values: FormData;
      step?: string;
    }
  >;
  setPersistentFormField: (
    formName: PersistentFormName,
    field: string,
    value: any
  ) => void;
  setPersistentFormData: (formName: PersistentFormName, data: FormData) => void;
  getPersistentFormData: (formName: PersistentFormName) => FormData;
  getCurrentPersistentStep: (formName: PersistentFormName) => string;
  setCurrentPersistentStep: (
    formName: PersistentFormName,
    step: string
  ) => void;
  resetPersistentForm: (formName: PersistentFormName) => void;
}

export const usePersistentFormsSlice = create<PersistentFormStore>()(
  persist(
    (set, get) => ({
      forms: {},
      setPersistentFormField: (formName, field, value) => {
        const prev = get().forms[formName]?.values || {};
        set((state) => ({
          forms: {
            ...state.forms,
            [formName]: {
              ...state.forms[formName],
              values: {
                ...prev,
                [field]: value,
              },
            },
          },
        }));
      },
      setPersistentFormData: (formName, data) => {
        set((state) => ({
          forms: {
            ...state.forms,
            [formName]: {
              ...state.forms[formName],
              values: data,
            },
          },
        }));
      },
      setCurrentPersistentStep: (formName, step) => {
        set((state) => ({
          forms: {
            ...state.forms,
            [formName]: {
              ...state.forms[formName],
              step,
            },
          },
        }));
      },
      getPersistentFormData: (formName) => get().forms[formName]?.values || {},
      getCurrentPersistentStep: (formName) =>
        get().forms[formName]?.step || InitialSteps[formName] || '',
      resetPersistentForm: (formName) => {
        const updated = { ...get().forms };
        delete updated[formName];
        set({ forms: updated });
      },
    }),
    {
      name: 'forms-persistent-store',
    }
  )
);
